import store from '@/store/store.js'
import { appType, appTypes } from '@/appType/appType.js'
import { persistDocumentHeightBaseline } from '@/utils/syncDocumentHeight.js'
import {
  canOpenTelegramExternalLink,
  isWithinTelegramLinkWindow,
  openTelegramExternalLink,
} from '@/utils/telegramExternalLink.js'
import { getTelegramPlatform } from '@/utils/telegramLaunchParams.js'

const STORAGE_KEY = 'bb_pending_payment'
const MODE_KEY = 'BB_PAYMENT_MODE'
const MAX_AGE_MS = 24 * 60 * 60 * 1000
// Android-клиент без try_browser открывает ссылку во встроенном браузере Telegram: там нет сохранённых
// карт и входа по Сбер ID. «Браузер по умолчанию» Telegram на Android открыть не умеет, Яндекс Браузера
// среди try_browser нет — берём самый распространённый Chrome. iOS по openLink уходит в браузер по умолчанию.
const ANDROID_BROWSER = 'chrome'

/** @type {{ tryBrowser?: string, leftApp: boolean } | null} */
let lastOutsideOpen = null
let isWatchingLeave = false

function safeGet(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // private mode / quota
  }
}

function safeRemove(key) {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

function rememberLeavingApp(event) {
  if (lastOutsideOpen && (event.type === 'blur' || document.visibilityState === 'hidden')) {
    lastOutsideOpen.leftApp = true
  }
}

function watchLeavingApp() {
  if (isWatchingLeave) {
    return
  }
  isWatchingLeave = true
  // Внешний браузер перекрывает Telegram: окно теряет фокус и страница уходит в hidden
  window.addEventListener('blur', rememberLeavingApp)
  document.addEventListener('visibilitychange', rememberLeavingApp)
}

/**
 * Открыть оплату во внешнем браузере. Вызывать только в окне после касания (isWithinTelegramLinkWindow):
 * тогда «Telegram не ушёл в фон» значит, что try_browser-браузера нет, и повторное «Открыть оплату»
 * идёт во встроенный браузер Telegram — иначе пользователь застрянет.
 */
async function openPaymentOutside(url, { isRetry = false } = {}) {
  const failedBefore = isRetry && lastOutsideOpen?.tryBrowser && !lastOutsideOpen.leftApp
  const isAndroid = /^android/i.test(getTelegramPlatform() || '')
  const tryBrowser = isAndroid && !failedBefore ? ANDROID_BROWSER : undefined

  if (!(await openTelegramExternalLink(url, { tryBrowser }))) {
    return false
  }

  lastOutsideOpen = { tryBrowser, leftApp: false }
  watchLeavingApp()
  return true
}

function openInsideMiniApp(url) {
  persistDocumentHeightBaseline()
  window.location.href = url
}

export function shouldOpenPaymentExternally() {
  const forced = safeGet(MODE_KEY)
  if (forced === 'webview') {
    return false
  }
  if (forced === 'external') {
    return appType === appTypes.TELEGRAM
  }

  // Не ждём загрузки SDK: решение нужно и при восстановлении шторки на старте.
  // Если openLink недоступен, openPaymentUrl откроет оплату внутри Mini App.
  return appType === appTypes.TELEGRAM
    && store.getters['app/isBodybaseProject']
}

export function getPendingExternalPayment() {
  try {
    const parsed = JSON.parse(safeGet(STORAGE_KEY) || 'null')
    if (!parsed?.paymentId || !parsed?.url) {
      return null
    }
    if (parsed.openedAt && Date.now() - Number(parsed.openedAt) > MAX_AGE_MS) {
      clearPendingExternalPayment()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearPendingExternalPayment() {
  safeRemove(STORAGE_KEY)
}

export async function reopenPendingExternalPayment() {
  const pending = getPendingExternalPayment()
  if (!pending?.url) {
    return false
  }
  if (!(await openPaymentOutside(pending.url, { isRetry: true }))) {
    clearPendingExternalPayment()
    openInsideMiniApp(pending.url)
  }
  return true
}

/**
 * Bodybase + Telegram: openLink во внешний браузер + шторка ожидания.
 * Иначе — прежний уход окна на payment_url.
 * @param {{ url: string, paymentId?: number|string, startedAt?: number }} params
 *   startedAt — время нажатия «Оплатить», от него отсчитывается окно openLink
 */
export async function openPaymentUrl({ url, paymentId, startedAt }) {
  if (!url) {
    return { external: false }
  }

  if (shouldOpenPaymentExternally() && await canOpenTelegramExternalLink()) {
    safeSet(STORAGE_KEY, JSON.stringify({
      paymentId,
      url,
      openedAt: Date.now(),
    }))

    // Бек отвечал дольше окна после касания — Telegram проигнорирует openLink.
    // Шторка попросит нажать «Открыть оплату»: это новое касание.
    const needsTap = !isWithinTelegramLinkWindow(startedAt)
    if (needsTap || await openPaymentOutside(url)) {
      store.dispatch('app/setMessageComponent', {
        messageComponent: 'PaymentResult',
        messageComponentData: {
          query: { payment_id: paymentId },
          external: true,
          needsTap,
        },
      })
      return { external: true }
    }

    clearPendingExternalPayment()
  }

  openInsideMiniApp(url)
  return { external: false }
}
